import { mapCollection, sortByNumber } from '../../utils/collection.utils';
import { parseNumber } from '../../utils/number.utils';
import { SchedulerTask } from '../scheduler-task';
import { SchedulerTaskPrint } from '../scheduler-task-print';
import { SchedulerTaskPrintConfiguration } from '../scheduler-task-print-configuration';
import { SchedulerTaskProgram } from '../scheduler-task-program';
import { SchedulerTaskProgramChecklist } from '../scheduler-task-program-checklist';
import { SchedulerTaskProgramConfiguration } from '../scheduler-task-program-configuration';
import { SchedulerTaskProgramsChecklist } from '../scheduler-task-programs-checklist';
import { SchedulerTaskSubTask } from '../scheduler-task-sub-task';
import { ManufacturingOrderBuilder } from './manufacturing-order.builder';
import { OperationModeBuilder } from './operation-mode.builder';
import { SummaryBuilder } from './summary.builder';
import { SchedulerTaskStatusBuilder } from './scheduler-task-status.builder';
import { WarningBuilder } from './warning.builder';

export class SchedulerTaskBuilder {
  static fromJson(json) {
    return (json)
      ? new SchedulerTask(
        json.hash,
        json.model,
        parseNumber(json.startDate),
        parseNumber(json.endDate),
        json.identifier,
        SchedulerTaskBuilder.buildPrintsFromList(json.prints),
        json.updatable,
        json.created,
        json.preparing,
        json.ready,
        json.error,
        json.issue,
        json.running,
        json.current,
        json.finished,
        json.putAdditionalTime,
        json.removeAdditionalTime,
        parseNumber(json.deviation),
        SchedulerTaskBuilder.buildSubTasksFromList(json.subTasks),
        json.started,
        json.hasParents,
        SchedulerTaskStatusBuilder.fromList(json.statuses),
        SchedulerTaskStatusBuilder.fromJson(json.activeStatus),
        json.project,
        json.deliveryDate,
        json.code,
        json.quantity,
        ManufacturingOrderBuilder.fromJson(json.manufacturingOrder),
        SchedulerTaskBuilder.buildProgramsFromList(json.schedulerTaskPrograms),
        WarningBuilder.fromJson(json.warning),
        json.pendingProgramDefinition,
        json.programDefinition,
        json.blocked,
        parseNumber(json.formatedStartDate),
        parseNumber(json.formatedEndDate),
        parseNumber(json.toolDeliveryDate),
        json.toolLocation,
        json.toolTable
      )
      : null;
  }

  static fromList(list) {
    return mapCollection(SchedulerTaskBuilder.fromJson, list);
  }

  static fromListOrdererd(list) {
    return sortByNumber('startDate', mapCollection(SchedulerTaskBuilder.fromJson, list));
  }

  static buildProgramsFromJson(json) {
    return (json)
      ? new SchedulerTaskProgram(
        json.hash,
        parseNumber(json.index),
        json.manufacturingOrderHash,
        json.manufacturingOrderCode,
        json.manufacturingOrderProjectNumber,
        json.materialHash,
        json.materialFileName,
        json.partHash,
        json.partName,
        json.programHash,
        json.programReference,
        parseNumber(json.programTime),
        parseNumber(json.programTimeDeviation),
        parseNumber(json.programTimeWithDeviation),
        json.operationHash,
        json.operationName,
        OperationModeBuilder.fromJson(json.operationMode),
        json.printHash,
        json.printReference,
        json.printProjectNumber,
        json.print,
        json.toolHash,
        json.toolName,
        json.toolHolderName,
        json.toolHolderHash,
        parseNumber(json.toolLength),
        parseNumber(json.crc),
        json.hasTwin,
        json.axle,
        parseNumber(json.pocket),
        json.observations,
        ManufacturingOrderBuilder.fromJson(json.manufacturingOrder),
        json.sourceName,
        json.camWorker,
        json.camObservations,
        json.executed,
        json.opposite
      )
      : null;
  }

  static buildProgramsFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildProgramsFromJson, list);
  }

  static buildPrintFromJson(json) {
    return (json)
      ? new SchedulerTaskPrint(
        json.printHash,
        json.printReference,
        json.printProjectNumber,
        json.materialHash,
        json.materialFileName,
        json.manufacturingOrderHash,
        json.manufacturingOrderCode,
        json.manufacturingOrderCustomer,
        json.manufacturingOrderReference,
        json.manufacturingOrderProject,
        json.manufacturingOrderMpReady,
        json.manufacturingOrderHasMaterialReady,
        json.manufacturingOrderHasMaterialInStock
      )
      : null;
  }

  static buildPrintsFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildPrintFromJson, list);
  }

  static buildSubTaskFromJson(json) {
    return (json)
      ? new SchedulerTaskSubTask(
        json.hash,
        json.identifier,
        json.current,
        json.running,
        json.sent,
        parseNumber(json.sendDate),
        parseNumber(json.programsDuration),
        json.isVersioned,
        SummaryBuilder.modelTimelinesFromList(json.modelTimelines)
      )
      : null;
  }

  static buildSubTasksFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildSubTaskFromJson, list);
  }

  static buildChecklistsFromJson(json) {
    return (json)
      ? new SchedulerTaskProgramsChecklist(
        parseNumber(json.pocketsNumber),
        json.reservedPockets,
        SchedulerTaskBuilder.buildChecklistsFromList(json.schedulerTaskProgramsChecklist)
      )
      : null;
  }

  static buildChecklistFromJson(json) {
    return (json)
      ? new SchedulerTaskProgramChecklist(
        json.hash,
        json.schedulerTaskProgramHash,
        json.schedulerTaskProgramIndex,
        json.toolHash,
        json.toolName,
        json.toolHolderHash,
        json.toolHolderName,
        json.programHash,
        json.programReference,
        parseNumber(json.programTime),
        parseNumber(json.pocket),
        parseNumber(json.originalPocket),
        json.checked,
        (json.ignorable || json.ignorableFixedTool || json.ignorableMasterTask),
        parseNumber(json.length),
        json.highlighted,
        (json.ignorable || json.ignorableFixedTool || json.ignorableMasterTask),
        json.ignorableFixedTool,
        json.twin,
        json.generatedTwin,
        json.ignorableMasterTask,
        json.masterTaskHash,
        json.masterTaskToolPosition,
        json.importedFromTaskHash,
        json.hasToolRebuildOption,
      )
      : null;
  }

  static buildChecklistsFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildChecklistFromJson, list);
  }

  static buildConfigurationFromJson(json) {
    return (json)
      ? new SchedulerTaskProgramConfiguration(
        json.hash,
        json.schedulerTaskProgramHash,
        json.originHash,
        parseNumber(json.index),
        parseNumber(json.vibration),
        json.refrigerationHash,
        json.checkBreak,
        parseNumber(json.repetitions),
        parseNumber(json.compensation),
        parseNumber(json.verticalCompensation)
      )
      : null;
  }

  static buildConfigurationFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildConfigurationFromJson, list);
  }

  static buildPrintConfigurationFromJson(json) {
    return (json)
      ? new SchedulerTaskPrintConfiguration(
        json.hash,
        json.originHash,
        json.printHash,
        json.printReference,
        parseNumber(json.userReference),
        parseNumber(json.x),
        parseNumber(json.y),
        parseNumber(json.z),
        parseNumber(json.c)
      )
      : null;
  }

  static buildPrintConfigurationFromList(list) {
    return mapCollection(SchedulerTaskBuilder.buildPrintConfigurationFromJson, list);
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/models/builders/scheduler-task.builder.ts